// src/pages/ApproveOrder.js
import React, { useEffect, useState, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { getOrInitStock, calculateClosing } from "../utils/stockUtils";
import { 
  collection, 
  getDocs, 
  query, 
  where, 
  addDoc, 
  doc, 
  increment,
  runTransaction,
  updateDoc,
  writeBatch,
  serverTimestamp,
  getDoc
} from "firebase/firestore";
import { getISTDate } from "../utils/dateUtils";
import { generateTripSheetPdf } from "../utils/pdfTripSheet";
import { db, auth } from "../firebase";
import Layout from "../components/Layout";
import { buildHsnSummary } from "../utils/hsnSummary";
import { generateChallanPdf } from "../utils/pdfChallan";
import { generateTaxInvoicePdf } from "../utils/pdfTaxInvoice";
import { supabase } from "../supabase";

const blobFromPdf = (pdfDoc) => {
  return pdfDoc.output("blob");
};

const openWhatsApp = (phone, message) => {
  if (!phone) {
    alert("Party phone number not available");
    return;
  }
  const cleanPhone = phone.replace(/\D/g, ""); 
  const encodedMsg = encodeURIComponent(message);
  const url = `https://wa.me/${cleanPhone}?text=${encodedMsg}`;
  window.open(url, "_blank");
};

const getISTDateStr = getISTDate;

function buildFinalOrderId(dateStr, seqNumber) {
  return `OK-${dateStr}-${String(seqNumber).padStart(4, "0")}`;
}

const normalizeAndMergeItems = (rawItems) => {
  const merged = {};
  Object.entries(rawItems || {}).forEach(([sku, qty]) => {
    const key = sku.trim().toUpperCase();
    const q = Number(qty || 0);
    if (q > 0) merged[key] = (merged[key] || 0) + q;
  });
  return merged;
};

function ApproveOrder() {
  const { orderId } = useParams();
  const navigate = useNavigate();
  
  const [date, setDate] = useState(getISTDate());
  const [skuMaster, setSkuMaster] = useState({});
  const [skuListOrdered, setSkuListOrdered] = useState([]);
  const [items, setItems] = useState({});
  const [order, setOrder] = useState(null);
  const [company, setCompany] = useState(null);
  const [vehicleNo, setVehicleno] = useState("-");
  const [address, setAddress] = useState("");
  const [displayOrderId, setDisplayOrderId] = useState("");
  const [loading, setLoading] = useState(true);
  const [approving, setApproving] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [showOnlyOrdered, setShowOnlyOrdered] = useState(false);

  const uploadPdfToSupabase = async (bucket, filePath, blob) => {
    const { error } = await supabase.storage.from(bucket).upload(filePath, blob, { contentType: "application/pdf", upsert: true });
    if (error) { console.error("Supabase upload error:", error); throw error; }
    const { data } = supabase.storage.from(bucket).getPublicUrl(filePath);
    return data.publicUrl;
  };
  
  const downloadBlob = (blob, fileName) => {
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = fileName;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  useEffect(() => {
    const loadAll = async () => {
      const refOrder = doc(db, "orders", orderId);
      const orderSnap = await getDoc(refOrder);
      if (!orderSnap.exists()) { navigate("/pending-orders"); return; }
      const oData = orderSnap.data();
      setOrder(oData);
      const currentClientId = oData.clientId || "pan";

      // 2. Load SKUs scoped to this order's client
      const skuSnap = await getDocs(query(
        collection(db, "skuMaster"), 
        where("clientId", "==", currentClientId)
      ));
      const skuArr = [];
      const skuMap = {};
      skuSnap.forEach((d) => {
        const data = d.data();
        if (!data.sku) return;
        const skuKey = data.sku.trim().toUpperCase();
        skuArr.push({ sku: skuKey, sortorder: Number(data.sortorder ?? 9999) });
        skuMap[skuKey] = data;
      });
      skuArr.sort((a, b) => a.sortorder - b.sortorder);
      setSkuMaster(skuMap);
      setSkuListOrdered(skuArr.map((x) => x.sku));

      const tempItems = {};
      Object.keys(skuMap).forEach((sku) => (tempItems[sku] = 0));
      Object.entries(oData.items || {}).forEach(([sku, qty]) => (tempItems[sku.trim().toUpperCase()] = Number(qty)));
      setItems(tempItems);

      if (oData.source === "DISTRIBUTOR_TO_DS" && oData.partyId) {
        const dsRef = query(
          collection(db, "dsMaster"), 
          where("clientId", "==", currentClientId),
          where("dsId", "==", oData.partyId)
        );
        const dsSnap = await getDocs(dsRef);
        if (!dsSnap.empty) {
          const dsData = dsSnap.docs[0].data();
          setVehicleno(dsData.vehicleNo || dsData.vehicleno || "-");
        }
      } else if (oData.partyId) {
        const pSnap = await getDoc(doc(db, "partyMaster", oData.partyId));
        if (pSnap.exists()) {
          const pData = pSnap.data();
          setVehicleno(pData.vehicleno || "");
          setAddress(pData.address || "");
        }
      }

      const cSnap = await getDoc(doc(db, "companyInfo", "default"));
      let finalCompany = cSnap.exists() ? cSnap.data() : {};
      if (oData.source === "DS_TO_OUTLET" && oData.dsId) {
          const dsRef = query(
            collection(db, "dsMaster"), 
            where("clientId", "==", currentClientId),
            where("dsId", "==", oData.dsId)
          );
          const dsSnap = await getDocs(dsRef);
          if (!dsSnap.empty) {
              const distId = dsSnap.docs[0].data().distributorId;
              if (distId) {
                  const distSnap = await getDoc(doc(db, "partyMaster", distId));
                  if (distSnap.exists()) {
                      const d = distSnap.data();
                      finalCompany = { ...finalCompany, name: d.name || "", address1: d.address || "", address2: d.route || "", phone: d.phone || "", gstin: d.gstno || "", place: d.place || "", pincode: d.pincode || "" };
                  }
              }
          }
      }
      setCompany(finalCompany);
      setDisplayOrderId(oData.displayOrderId || oData.tempOrderId || oData.orderNo || orderId);
      setLoading(false);
    };
    loadAll();
  }, [orderId, navigate]);

  const itemsForUI = useMemo(() => {
    const obj = {};
    Object.keys(skuMaster).forEach((sku) => (obj[sku] = Number(items[sku] || 0)));
    return obj;
  }, [items, skuMaster]);

  const mergedItemsForSave = useMemo(() => normalizeAndMergeItems(items), [items]);

  const updateQty = (sku, val) => {
    setItems({ ...items, [sku]: Number(val) });
  };

  const computeTotals = () => {
    let tQty = 0, tBasic = 0, tGST = 0, tCESS = 0, tAddl = 0, tGrand = 0;
    Object.entries(mergedItemsForSave).forEach(([sku, qty]) => {
      const m = skuMaster[sku] || {};
      let rate = Number(m.Basic_Eway || 0);
      if (order?.source === "DISTRIBUTOR_TO_DS") rate = Number(m.dsBasicRate || m.Basic_Eway || 0);
      else if (order?.source === "DISTRIBUTOR_APP") rate = Number(m.distributorBasicRate || m.Basic_Eway || 0);
      const BASIC = qty * rate;
      
      const gstRate = Number(m.gst || 0) || 5;
      const GST = BASIC * (gstRate / 100);
      
      const CESS = BASIC * ((Number(m.cess) || 0) / 100);
      const ADDL = qty * (Number(m.addlcess) || 0);
      tQty += qty; tBasic += BASIC; tGST += GST; tCESS += CESS; tAddl += ADDL;
      tGrand += BASIC + GST + CESS + ADDL;
    });
    return { totalQty: tQty, totalBasic: tBasic, totalGST: tGST, totalCESS: tCESS, totalAddlCess: tAddl, grandTotal: tGrand };
  };

  const computeTotalsTrip = () => {
    let tQty = 0, tBasic = 0, tGST = 0, tCESS = 0, tAddl = 0, tGrand = 0;
    Object.entries(mergedItemsForSave).forEach(([sku, qty]) => {
      const m = skuMaster[sku] || {};
      let rate = Number(m.Basic_Eway || 0);
      if (order?.source === "DISTRIBUTOR_TO_DS") rate = Number(m.dsBasicRate || m.Basic_Eway || 0);
      else if (order?.source === "DISTRIBUTOR_APP") rate = Number(m.distributorBasicRate || m.Basic_Eway || 0);
      const BASIC = qty * rate;
      
      const gstRate = Number(m.gst || 0) || 5;
      const GST = BASIC * (gstRate / 100);
      
      const CESS = BASIC * ((Number(m.cess) || 0) / 100);
      const ADDL = qty * (Number(m.addlcess) || 0);
      const REBATE = qty * (Number(m.Rebate) || 0);
      tQty += qty; tBasic += BASIC; tGST += GST; tCESS += CESS; tAddl += ADDL;
      tGrand += BASIC + GST + CESS + ADDL + REBATE;
    });
    return { totalQty: tQty, totalBasic: tBasic, totalGST: tGST, totalCESS: tCESS, totalAddlCess: tAddl, grandTotal: tGrand };
  };

  const totals = computeTotals();
  const totalstrip = computeTotalsTrip();

  const approveOrder = async () => {
    if (approving) return;
    try {
      setApproving(true);
      const totals = computeTotals();
      const totalstrip = computeTotalsTrip();
      const approvedAtLocal = new Date();
      let partyPhone = "";
      if (order.partyId) {
        const partySnap = await getDoc(doc(db, "partyMaster", order.partyId));
        if (partySnap.exists()) partyPhone = partySnap.data().phone || "";
      }
      if (partyPhone && partyPhone.length === 10) partyPhone = "91" + partyPhone;

      const dateStr = getISTDate().replace(/-/g, "").slice(2); // YYMMDD format
      const counterRef = doc(db, "counters", "orderSequence");
      const seq = await runTransaction(db, async (tx) => {
        const snap = await tx.get(counterRef);
        let next = 1;
        if (snap.exists() && snap.data().date === dateStr) next = snap.data().lastSeq + 1;
        tx.set(counterRef, { date: dateStr, lastSeq: next }, { merge: true });
        return next;
      });

      const finalOrderId = buildFinalOrderId(dateStr, seq);
      const dateOnly = new Date().toISOString().slice(0, 10);
      const finalVehicleNo = vehicleNo || order.vehicleNo || "";
      const finalAddress = address || "";

      const legacyOrderLines = Object.entries(mergedItemsForSave).filter(([, qty]) => qty > 0).map(([sku, qty], index) => {
        const m = skuMaster[sku] || {};
        let rate = Number(m.Basic_Eway || 0);
        if (order?.source === "DISTRIBUTOR_TO_DS") rate = Number(m.dsBasicRate || m.Basic_Eway || 0);
        else if (order?.source === "DISTRIBUTOR_APP") rate = Number(m.distributorBasicRate || m.Basic_Eway || 0);
        const basic = qty * rate;
        const gstRate = Number(m.gst || 0) || 5;
        const gst = basic * (gstRate / 100);
        const cess = basic * ((Number(m.cess) || 0) / 100);
        const addl = qty * (Number(m.addlcess) || 0);
        const rebate = qty * (Number(m.Rebate) || 0);
        return { lineNo: index + 1, sku, itemCode: m.ItemCode || "", skuName: m.Name || "", description: m.description || "", brand: m.Brand || "", msku: m.msku || "", segment: m.segment || "", qty, rate, basicAmount: basic, gstAmount: gst, cessAmount: cess, addlCessAmount: addl, rebateAmount: rebate, totalAmount: basic + gst + cess + addl + rebate, ewayAmount: basic + gst + cess + addl, hsn: String(m.HSN || m.hsn || m.Hsn || "").trim() };
      });

      const approvedDateOnly = getISTDate();
      const approvedAtTs = serverTimestamp();
      let partySnapData = {};
      if (order.partyId) {
        const pSnap = await getDoc(doc(db, "partyMaster", order.partyId));
        if (pSnap.exists()) partySnapData = pSnap.data();
      }
      const finalGstin = order.gstin || partySnapData.gstno || "";

      const flatOrderLines = Object.entries(mergedItemsForSave).filter(([, qty]) => qty > 0).map(([sku, qty]) => {
        const m = skuMaster[sku] || {};
        let basicRate = Number(m.Basic_Eway || 0);
        if (order?.source === "DISTRIBUTOR_TO_DS") basicRate = Number(m.dsBasicRate || m.Basic_Eway || 0);
        else if (order?.source === "DISTRIBUTOR_APP") basicRate = Number(m.distributorBasicRate || m.Basic_Eway || 0);
        const basicAmount = qty * basicRate;
        const gstRate = Number(m.gst || 0) || 5;
        const gstAmount = basicAmount * (gstRate / 100);
        const cessAmount = basicAmount * (Number(m.cess || 0) / 100);
        const addlCessAmount = qty * Number(m.addlcess || 0);
        const rebateAmount = qty * Number(m.Rebate || 0);
        return { orderId, displayOrderId: finalOrderId, approvedAt: new Date(), approvedDateOnly, source: order.source || "", status: "APPROVED", vehicleNo: finalVehicleNo, partyId: order.partyId || "", partyName: order.partyName || "", partyPhone, partyType: partySnapData.partytype || "", creditLimit: partySnapData.creditLimit || 0, gstin: finalGstin, sku, itemCode: m.ItemCode || "", skuName: m.Name || "", hsn: String(m.HSN || m.hsn || m.Hsn || "").trim(), qty, basicRate, basicAmount, gstAmount, cessAmount, addlCessAmount, rebateAmount, lineTotal: basicAmount + gstAmount + cessAmount + addlCessAmount + rebateAmount };
      });

      await updateDoc(doc(db, "orders", orderId), {
        status: "APPROVED", approvedAt: approvedAtTs, approvedDateOnly, orderDate: approvedAtTs, displayOrderId: finalOrderId, partyPhone, items: mergedItemsForSave, orderLines: legacyOrderLines, totalQty: totals.totalQty, ewayAmount: totals.grandTotal, totalAmount: totalstrip.grandTotal, vehicleNo: finalVehicleNo, address: finalAddress, gstin: finalGstin
      });

      const batch = writeBatch(db);
      flatOrderLines.forEach((line) => batch.set(doc(collection(db, "orderLines")), line));
      // 3. Update Stock (Properly nested for internal tracking)
      const todayStr = approvedDateOnly;
      if (order?.source === "DISTRIBUTOR_TO_DS") {
            // Ensure DS stock is initialized with carry-forward
            await getOrInitStock("ds", order.partyId, todayStr, order.clientId || "pan");
            
            const dsStockRef = doc(db, "ds_stock_daily", `${order.partyId}_${todayStr}`);
            batch.set(dsStockRef, { 
                dsId: order.partyId, 
                date: todayStr, 
                clientId: order.clientId || "pan", 
                initialized: true 
            }, { merge: true });
            const dsInv = {};
            Object.entries(mergedItemsForSave).forEach(([sku, qty]) => { 
                const skuUpper = sku.trim().toUpperCase();
                dsInv[`inventory.${skuUpper}.received`] = increment(qty); 
            });
            batch.update(dsStockRef, dsInv);

            if (order.distributorId) {
                // Ensure Distributor stock is initialized with carry-forward
                await getOrInitStock("distributor", order.distributorId, todayStr, order.clientId || "pan");

                const distStockRef = doc(db, "distributor_stock_daily", `${order.distributorId}_${todayStr}`);
                batch.set(distStockRef, { 
                    distributorId: order.distributorId, 
                    date: todayStr, 
                    clientId: order.clientId || "pan" 
                }, { merge: true });
                const distInv = {};
                Object.entries(mergedItemsForSave).forEach(([sku, qty]) => { 
                    const skuUpper = sku.trim().toUpperCase();
                    distInv[`inventory.${skuUpper}.sold`] = increment(qty); 
                });
                batch.update(distStockRef, distInv);

                // ** LOG TO ds_orders ON APPROVAL **
                const loadRef = doc(collection(db, "ds_orders"));
                batch.set(loadRef, {
                    ...order,
                    orderId: orderId,
                    displayOrderId: finalOrderId,
                    status: "APPROVED",
                    approvedAt: serverTimestamp(),
                    approvedDateOnly: todayStr, // ADDED for RSM Reports
                    createdAt: serverTimestamp(),
                    type: "DS_LOAD_AUTHORIZED"
                });

            }

      } else if (order?.source === "DISTRIBUTOR_APP") {
            // Ensure Distributor stock is initialized (e.g. for HUB -> DISTRIBUTOR receipts)
            await getOrInitStock("distributor", order.partyId, todayStr, order.clientId || "pan");

            const distStockRef = doc(db, "distributor_stock_daily", `${order.partyId}_${todayStr}`);
            batch.set(distStockRef, { 
                distributorId: order.partyId, 
                date: todayStr, 
                clientId: order.clientId || "pan" 
            }, { merge: true });
            const distInv = {};
            let totalReceived = 0;
            Object.entries(mergedItemsForSave).forEach(([sku, qty]) => { 
                const skuUpper = sku.trim().toUpperCase();
                distInv[`inventory.${skuUpper}.received`] = increment(qty); 
                totalReceived += Number(qty);
            });
            batch.update(distStockRef, distInv);

            // Update real-time month aggregates for Hub
            const monthId = todayStr.slice(0, 7);
            const aggRef = doc(db, "distributor_aggregates", `${order.partyId}_${monthId}`);
            batch.set(aggRef, { 
                mtdPurchaseUnits: increment(totalReceived),
                mtdStockReceivedUnits: increment(totalReceived) // Tracking total units added to stock
            }, { merge: true });
      } else if (order?.source === "DS_TO_OUTLET") {
            const dsId = order.dsId || order.partyId || "";
            // Ensure DS stock is initialized
            await getOrInitStock("ds", dsId, todayStr, order.clientId || "pan");

            const dsStockRef = doc(db, "ds_stock_daily", `${dsId}_${todayStr}`);
            batch.set(dsStockRef, { 
                dsId, 
                date: todayStr, 
                clientId: order.clientId || "pan", 
                initialized: true 
            }, { merge: true });
            const dsInv = {};
            Object.entries(mergedItemsForSave).forEach(([sku, qty]) => { 
                const skuUpper = sku.trim().toUpperCase();
                dsInv[`inventory.${skuUpper}.sold`] = increment(qty); 
            });
            batch.update(dsStockRef, dsInv);
      }
      await batch.commit();

      // 5. Cloud Storage (Supabase) with Fallback
      let tripUrl = "", challanUrl = "", invoiceUrl = "";
      
      try {
        const tripPdfDoc = generateTripSheetPdf(finalOrderId, { ...order, displayOrderId: finalOrderId, orderDateOnly: dateOnly, items: mergedItemsForSave, orderLines: legacyOrderLines }, mergedItemsForSave, skuMaster, totalstrip, "Trip Sheet");
        const tripBlob = blobFromPdf(tripPdfDoc);
        downloadBlob(tripBlob, `TripSheet-${finalOrderId}.pdf`);
        try {
          tripUrl = await uploadPdfToSupabase("tripsheets", `TripSheet-${finalOrderId}.pdf`, tripBlob);
        } catch (uerr) { console.warn("TripSheet upload failed, proceeding with local download only."); }
      } catch (perr) { console.error("TripSheet logic failed:", perr); }

      try {
        const hsnSum = buildHsnSummary(mergedItemsForSave, skuMaster, order?.source);
        const challanPdfDoc = generateChallanPdf(finalOrderId, { ...order, displayOrderId: finalOrderId, orderDateOnly: dateOnly, vehicleNo: finalVehicleNo, items: mergedItemsForSave, orderLines: legacyOrderLines }, hsnSum, totals, company);
        const challanBlob = blobFromPdf(challanPdfDoc);
        downloadBlob(challanBlob, `Challan-${finalOrderId}.pdf`);
        try {
          challanUrl = await uploadPdfToSupabase("challans", `Challan-${finalOrderId}.pdf`, challanBlob);
        } catch (uerr) { console.warn("Challan upload failed."); }

        const invoicePdfDoc = generateTaxInvoicePdf(finalOrderId, { ...order, displayOrderId: finalOrderId, orderDateOnly: dateOnly, vehicleNo: finalVehicleNo }, hsnSum, totals, company);
        const invoiceBlob = blobFromPdf(invoicePdfDoc);
        downloadBlob(invoiceBlob, `Invoice-${finalOrderId}.pdf`);
        try {
          invoiceUrl = await uploadPdfToSupabase("invoices", `Invoice-${finalOrderId}.pdf`, invoiceBlob);
        } catch (uerr) { console.warn("Invoice upload failed."); }
      } catch (perr) { console.error("PDF generation failed:", perr); }

      // Final Database Update (Cloud URLs are optional)
      await updateDoc(doc(db, "orders", orderId), { 
        tripSheetPdfUrl: tripUrl, 
        challanPdfUrl: challanUrl, 
        taxInvoicePdfUrl: invoiceUrl 
      });
      
      alert("Order Authorized Successfully! Documents downloaded to your device.");
      navigate("/pending-orders");
    } catch (e) { 
        console.error("Authorization Error:", e); 
        alert("Authorize failed: " + (e.message || "Unknown error")); 
    } finally { 
        setApproving(false); 
    }
  };

  const generateTaxInvoice = async () => {
    try {
      const hsnSum = buildHsnSummary(mergedItemsForSave, skuMaster, order?.source);
      const pdfDoc = generateTaxInvoicePdf(order.displayOrderId, { ...order, items: mergedItemsForSave, orderDateOnly: order.approvedDateOnly }, hsnSum, computeTotals(), company);
      const url = await uploadPdfToSupabase("invoices", `Invoice-${order.displayOrderId}.pdf`, blobFromPdf(pdfDoc));
      await updateDoc(doc(db, "orders", orderId), { taxInvoicePdfUrl: url });
      window.open(url, "_blank");
    } catch (err) { alert("Tax Invoice generation failed"); }
  };

  const generateDeliveryChallan = async () => {
    try {
      const hsnSum = buildHsnSummary(mergedItemsForSave, skuMaster, order?.source);
      const pdfDoc = generateChallanPdf(order.displayOrderId, { ...order, items: mergedItemsForSave }, hsnSum, computeTotals(), company);
      const url = await uploadPdfToSupabase("challans", `Challan-${order.displayOrderId}.pdf`, blobFromPdf(pdfDoc));
      await updateDoc(doc(db, "orders", orderId), { challanPdfUrl: url });
      window.open(url, "_blank");
    } catch (err) { alert("Challan generation failed"); }
  };

  const generateTempTripSheet = () => {
    try {
      const tempId = order.displayOrderId || order.tempOrderId || orderId;
      const pdfDoc = generateTripSheetPdf(tempId, { ...order, orderDate: new Date().toISOString() }, items, skuMaster, computeTotalsTrip(), "Temp Trip Sheet");
      const url = URL.createObjectURL(blobFromPdf(pdfDoc));
      const a = document.createElement("a"); a.href = url; a.download = `TempTripSheet-${tempId}.pdf`; a.click();
    } catch (err) { alert("Temp Trip Sheet failed"); }
  };

  const saveChanges = async () => {
    try {
      await updateDoc(doc(db, "orders", orderId), { items: mergedItemsForSave, totalQty: totals.totalQty, totalAmount: totalstrip.grandTotal, vehicleNo, address, updatedAt: serverTimestamp() });
      alert("Draft saved successfully.");
    } catch (err) { alert("Save failed"); }
  };

  if (loading || !order) {
    return (
      <Layout>
        <div style={{ padding: 100, textAlign: "center", color: "var(--text-muted)", animation: "pulse 2s infinite" }}>
          <div style={{ fontSize: 40, marginBottom: 20 }}>🧠</div>
          <div style={{ fontWeight: 800, letterSpacing: "0.05em", textTransform: "uppercase", fontSize: 12 }}>Initializing Strategic Command Center...</div>
        </div>
      </Layout>
    );
  }

  const filteredSkuList = skuListOrdered.filter(sku => {
    const m = skuMaster[sku] || {};
    const matchesSearch = (m.Name || "").toLowerCase().includes(searchTerm.toLowerCase()) || sku.toLowerCase().includes(searchTerm.toLowerCase());
    const isOrdered = itemsForUI[sku] > 0;
    if (showOnlyOrdered) return matchesSearch && isOrdered;
    return matchesSearch;
  });

  return (
    <Layout>
      <div className="layout-content">
        {/* ACTION BAR */}
        <div style={{ marginBottom: 32, display: "flex", justifyContent: "space-between", alignItems: "center", background: "var(--surface)", padding: "24px 32px", borderRadius: 24, boxShadow: "var(--shadow-md)", border: "1px solid var(--border)", position: "sticky", top: 0, zIndex: 100, backdropFilter: "blur(20px)" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 24 }}>
             <button onClick={() => navigate(-1)} className="btn-secondary" style={{ padding: 12, borderRadius: 12 }}>←</button>
             <div>
                <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 4 }}>
                    <h1 style={{ margin: 0, fontSize: 24, fontWeight: 900 }}>{displayOrderId}</h1>
                    <span className={`badge ${order.status === "APPROVED" ? "badge-success" : "badge-warning"}`}>{order.status || "PENDING"}</span>
                </div>
                <div style={{ fontSize: 16, fontWeight: 700, color: "var(--text-muted)" }}>{order.partyName}</div>
             </div>
          </div>
          <div style={{ display: "flex", gap: 16 }}>
            <button onClick={saveChanges} className="btn-secondary" style={{ padding: "12px 24px" }}>SAVE DRAFT</button>
            {order?.source === "DS_TO_OUTLET" && (
                <button
                    onClick={async () => {
                        const { generateOrderInvoicePdf } = await import("../utils/pdfOrderInvoice");
                        const doc = generateOrderInvoicePdf({ ...order, items: mergedItemsForSave, distributorName: company.name }, skuMaster, company);
                        doc.save(`Receipt-${displayOrderId}.pdf`);
                    }}
                    className="btn-secondary"
                    style={{ padding: "12px 24px", color: "var(--primary)", borderColor: "var(--primary)" }}
                >
                    ⎙ SIMPLIFIED RECEIPT
                </button>
            )}
            <button
              onClick={approveOrder}
              disabled={approving || order.status === "APPROVED"}
              className={`btn-primary ${order.status === "APPROVED" ? "success" : ""}`}
              style={{ padding: "12px 40px", background: order.status === "APPROVED" ? "var(--success)" : "var(--primary)" }}
            >
              {approving ? "VERIFYING..." : order.status === "APPROVED" ? "✓ AUTHORIZED" : "⚡ AUTHORIZE ORDER"}
            </button>
          </div>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 380px", gap: 32 }}>
          <div style={{ display: "flex", flexDirection: "column", gap: 32 }}>
            <div className="premium-card" style={{ display: "grid", gridTemplateColumns: "1fr 1.2fr", gap: 24, padding: 32 }}>
              <div>
                <label style={{ display: "block", fontSize: 10, fontWeight: 800, color: "var(--text-muted)", marginBottom: 8, textTransform: "uppercase" }}>Vehicle Assignment</label>
                <input className="form-input" value={vehicleNo} onChange={e => setVehicleno(e.target.value.toUpperCase())} placeholder="REGISTRATION NO" style={{ width: "100%", fontWeight: 800 }} />
              </div>
              
              <div style={{ borderLeft: "1px solid var(--border)", paddingLeft: 24, display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
                 <div>
                    <label style={{ display: "block", fontSize: 10, fontWeight: 800, color: "var(--text-muted)", marginBottom: 4 }}>Ingestion</label>
                    <div style={{ fontSize: 13, fontWeight: 800, color: "var(--primary)" }}>{order.source || "MANUAL"}</div>
                 </div>
                 <div>
                    <label style={{ display: "block", fontSize: 10, fontWeight: 800, color: "var(--text-muted)", marginBottom: 4 }}>Timestamp</label>
                    <div style={{ fontSize: 13, fontWeight: 800 }}>{order.orderDateOnly || "-"}</div>
                 </div>
              </div>
            </div>

            <div className="premium-card" style={{ padding: 0, overflow: "hidden" }}>
              <div style={{ padding: "20px 32px", borderBottom: "1px solid var(--border)", background: "var(--surface-hover)", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 24 }}>
                    <span style={{ fontSize: 13, fontWeight: 800 }}>ITEMIZED MANIFEST</span>
                    <div style={{ position: "relative" }}>
                        <input type="text" className="form-input" placeholder="Find SKU..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} style={{ padding: "8px 12px 8px 36px", fontSize: 13, width: 200 }} />
                        <span style={{ position: "absolute", left: 12, top: 8 }}>🔍</span>
                    </div>
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
                    <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12, fontWeight: 700, cursor: "pointer" }}>
                        <input type="checkbox" checked={showOnlyOrdered} onChange={(e) => setShowOnlyOrdered(e.target.checked)} /> Ordered Only
                    </label>
                </div>
              </div>
              <table className="enterprise-table">
                <thead>
                <tr>
                    <th style={{ paddingLeft: 32 }}>SKU DETAIL</th>
                    <th style={{ textAlign: "center" }}>QTY</th>
                    <th style={{ textAlign: "right" }}>RATE</th>
                    <th style={{ textAlign: "right" }}>TAXABLE</th>
                    <th style={{ textAlign: "right" }}>GST</th>
                    <th style={{ textAlign: "right", paddingRight: 32 }}>TOTAL</th>
                </tr>
                </thead>
                <tbody>
                {filteredSkuList.map((sku) => {
                    const m = skuMaster[sku] || {};
                    const qty = itemsForUI[sku];
                    let rate = Number(m.Basic_Eway || 0);
                    if (order?.source === "DISTRIBUTOR_TO_DS") rate = Number(m.dsBasicRate || m.Basic_Eway || 0);
                    else if (order?.source === "DISTRIBUTOR_APP") rate = Number(m.distributorBasicRate || m.Basic_Eway || 0);
                    const BASIC = qty * rate;
                    const gstRate = Number(m.gst || 0) || 5;
                    const GST = BASIC * (gstRate / 100);
                    const CESS = BASIC * (Number(m.cess || 0) / 100);
                    const ADDL = qty * Number(m.addlcess || 0);
                    const REBATE = qty * Number(m.Rebate || 0);
                    const LINE_TOTAL = BASIC + GST + CESS + ADDL + REBATE;
                    return (
                    <tr key={sku} style={{ background: qty > 0 ? "rgba(255, 107, 0, 0.02)" : "transparent" }}>
                        <td style={{ paddingLeft: 32 }}>
                            <div style={{ fontWeight: 800 }}>{m.Name || sku}</div>
                            <div style={{ fontSize: 10, color: "var(--text-muted)" }}>{sku} • HSN: {m.hsn}</div>
                        </td>
                        <td style={{ textAlign: "center" }}>
                            <input type="number" className="form-input" value={qty} onChange={(e) => updateQty(sku, e.target.value)} style={{ width: 60, textAlign: "center", fontWeight: 800 }} />
                        </td>
                        <td style={{ textAlign: "right" }}>₹{rate.toFixed(2)}</td>
                        <td style={{ textAlign: "right" }}>₹{BASIC.toFixed(2)}</td>
                        <td style={{ textAlign: "right" }}>₹{GST.toFixed(2)}</td>
                        <td style={{ textAlign: "right", fontWeight: 800, paddingRight: 32 }}>₹{LINE_TOTAL.toFixed(2)}</td>
                    </tr>
                    );
                })}
                </tbody>
              </table>
            </div>
          </div>

          <div style={{ display: "flex", flexDirection: "column", gap: 32 }}>
            <div className="premium-card">
              <h3 style={{ margin: "0 0 20px 0", fontSize: 12, fontWeight: 800, color: "var(--text-muted)" }}>DOCUMENTS</h3>
              <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                {order.status === "APPROVED" ? (
                  <>
                    <button onClick={generateTaxInvoice} className="btn-secondary" style={{ justifyContent: "flex-start", gap: 12, padding: "14px 20px" }}>🧾 Invoice</button>
                    <button onClick={generateDeliveryChallan} className="btn-secondary" style={{ justifyContent: "flex-start", gap: 12, padding: "14px 20px" }}>📦 Challan</button>
                  </>
                ) : (
                  <div style={{ padding: 12, borderRadius: 12, background: "var(--surface-hover)", fontSize: 11, color: "var(--text-muted)", textAlign: "center", border: "1px dashed var(--border)" }}>
                    Official Documents (Invoice/Challan) will be generated upon Authorization.
                  </div>
                )}
                <button onClick={generateTempTripSheet} className="btn-secondary" style={{ justifyContent: "flex-start", gap: 12, padding: "14px 20px" }}>📋 {order.status === "APPROVED" ? "Final Trip Sheet" : "Temp Trip Sheet"}</button>
              </div>
            </div>

            <div className="premium-card" style={{ background: "var(--text-main)", color: "white", padding: 32 }}>
              <h3 style={{ margin: "0 0 24px 0", fontSize: 12, fontWeight: 800, opacity: 0.5 }}>VALUATION</h3>
              <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
                <div style={{ display: "flex", justifyContent: "space-between" }}><span>Taxable</span><span>₹{totals.totalBasic.toFixed(2)}</span></div>
                <div style={{ display: "flex", justifyContent: "space-between" }}><span>GST</span><span>₹{totals.totalGST.toFixed(2)}</span></div>
                <div style={{ height: 1, background: "rgba(255,255,255,0.1)" }}></div>
                <div style={{ textAlign: "right" }}>
                    <div style={{ fontSize: 32, fontWeight: 900, color: "var(--success)" }}>₹{totalstrip.grandTotal.toFixed(2)}</div>
                    <div style={{ fontSize: 10, opacity: 0.5 }}>{totals.totalQty} Packets Dispatched</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
}

export default ApproveOrder;
